import React, { useState } from 'react';
import { HelpCircle, MessageSquare, Ticket } from 'lucide-react';
import Modal from '../ui/Modal';
import SupportContactForm from './SupportContactForm';
import SupportTicketHistory from './SupportTicketHistory';
import { SupportTicket } from '../../types';

interface HelpSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: 'overview' | 'contact' | 'tickets';
}

type SupportView = 'overview' | 'contact' | 'tickets';

export default function HelpSupportModal({ 
  isOpen, 
  onClose, 
  initialView = 'overview' 
}: HelpSupportModalProps) {
  const [currentView, setCurrentView] = useState<SupportView>(initialView);
  const [, setSelectedTicket] = useState<SupportTicket | null>(null);

  // Reset view when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setCurrentView(initialView);
      setSelectedTicket(null);
    }
  }, [isOpen, initialView]);

  const handleContactSuccess = () => {
    // Switch to tickets view to show the newly created ticket
    setCurrentView('tickets');
  };

  const handleTicketSelect = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    // Future: Could expand to show ticket details in a separate view
  };

  const renderOverview = () => (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          How can we help you?
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Get support, submit feedback, or view your existing tickets.
        </p>
      </div>

      <div className="grid gap-4">
        {/* Contact Support Option */}
        <div
          onClick={() => setCurrentView('contact')}
          className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer
                   hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200
                   bg-white dark:bg-gray-800"
        >
          <div className="flex items-start">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-4">
              <MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">
                Contact Support
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Submit a new support ticket for technical issues, account problems, or general questions.
              </p>
            </div>
          </div>
        </div>

        {/* View Tickets Option */}
        <div
          onClick={() => setCurrentView('tickets')}
          className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer
                   hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200
                   bg-white dark:bg-gray-800"
        >
          <div className="flex items-start">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mr-4">
              <Ticket className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">
                My Support Tickets
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                View the status and history of your submitted support tickets.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Help Section */}
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-start">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg mr-4">
              <HelpCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">
                Quick Help
              </h4>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <p>• <strong>Habit not saving?</strong> Check your internet connection and try again.</p>
                <p>• <strong>Missing notifications?</strong> Check your browser notification settings.</p>
                <p>• <strong>Streak calculation issues?</strong> Ensure your timezone is set correctly in your profile.</p>
                <p>• <strong>Billing questions?</strong> Contact support with your subscription details.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="flex border-b border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setCurrentView('overview')}
        className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
          currentView === 'overview'
            ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
      >
        Help & Support
      </button>
      <button
        onClick={() => setCurrentView('contact')}
        className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
          currentView === 'contact'
            ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
      >
        Contact Support
      </button>
      <button
        onClick={() => setCurrentView('tickets')}
        className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
          currentView === 'tickets'
            ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
      >
        My Tickets
      </button>
    </div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'overview':
        return renderOverview();
      case 'contact':
        return (
          <SupportContactForm
            onSuccess={handleContactSuccess}
            onCancel={() => setCurrentView('overview')}
          />
        );
      case 'tickets':
        return (
          <SupportTicketHistory
            onTicketSelect={handleTicketSelect}
          />
        );
      default:
        return renderOverview();
    }
  };

  const getModalTitle = () => {
    switch (currentView) {
      case 'contact':
        return 'Contact Support';
      case 'tickets':
        return 'Support Tickets';
      default:
        return 'Help & Support';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      size="lg"
    >
      <div className="flex flex-col max-h-[80vh]">
        {/* Navigation */}
        {renderNavigation()}
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>
      </div>
    </Modal>
  );
}
